import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useQuery } from "../../../AppLayout";
import ChartTypeTabs from "./tabs/ChartTypeTabs";
import ChartLoading from "../ChartLoading";
import ReportHeader from "./ReportHeader";
import { getChartType, iconList } from "../../ReportSetting";
import { UnifiedChartData } from "../charts/types";
import {
  datetimeFields,
  filterDataGoalChart,
  getMeasureByLabel,
  getValueKey,
  getViewByLabel,
  handleDataBarChart,
  handleDataGoalChart,
  handleDataLineChart,
  handleDataPieChart,
} from "../../DataUtils";
import { defaultGoal } from "../../../reducers/goalReducer";
import BarChart from "../charts/BarChart";
import { colors as defaultColors } from "../../../board/components/TaskDetail/ColorPicker";
import LineAreaChart from "../charts/LineAreaChart";
import DonutChart from "../charts/DonutChart";
import GoalBarChart from "../charts/GoalBarChart";
import MetricCard from "../MetricCard";
import {
  useAggregatedData,
  useAggregatedDataCompare,
  useNumberData,
} from "../../hooks/useReportData";

interface ChartViewProps {
  projectId: string;
  dashboardId: string;
  reportId: string;
}

const ChartView = ({ projectId, dashboardId, reportId }: ChartViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentParams = useQuery();

  const reportType = currentParams.get("reportType") || "";
  const reportChartType = currentParams.get("reportChartType") || "";
  const reportObjectType = currentParams.get("reportObjectType") || "SALE";
  const reportViewMode = currentParams.get("reportViewMode") || "";

  const ruleReducer = useSelector((state: any) => state.ruleReducer);
  const goals = useSelector((state: any) => state.goal.get("goals"));
  const currency = useSelector((state: any) => state.projectReducer.getIn(["project", "currency"]));
  const reportData = useSelector((state: any) => state.reportSetting.get("data"));

  const viewBy = reportData.get("nameKey");
  const measureBy = reportData.get("valueKey");
  const segmentBy = reportData.get("segmentBy");
  const dateUnit = reportData.get("dateUnit");
  const comparisonBy = reportData.get("comparisonBy");
  const filters = reportData.get("filters") || fromJS({});
  const icons = reportData.get("icons")?.toJS() || [];
  const colors = reportData.get("colors")?.toJS() || [];
  const goalId = reportData.get("goalId");

  const chartIcons = icons.length > 0 ? icons : [iconList[0].value];
  const chartColors = colors.length > 0 ? colors : defaultColors;

  const viewModeEnabled = reportViewMode === "true";
  const chartType = getChartType(reportType, reportChartType);

  const [goalFilters, setGoalFilters] = useState<any>(fromJS({}));
  const initChartData: UnifiedChartData = {
    labels: ["None"],
    series: [
      {
        dataKey: "None",
        name: "None",
        data: [0],
      },
    ],
  };
  const [unifiedChartData, setUnifiedChartData] = useState<UnifiedChartData>(initChartData);

  const shouldUseComparison = comparisonBy !== "none";

  const { data: dataAggregated, isLoading: isLoadingAggregated } = useAggregatedData(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    chartType == "Goal" ? goalFilters : filters,
    reportObjectType,
    chartType !== "Table" &&
      chartType !== "Number" &&
      !(shouldUseComparison && chartType === "Line")
  );

  const { data: dataCompare } = useAggregatedDataCompare(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    filters,
    reportObjectType,
    comparisonBy,
    shouldUseComparison && chartType === "Line"
  );

  const { data: dataNumber, isLoading: isLoadingNumber } = useNumberData(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    getValueKey(measureBy),
    filters,
    reportObjectType,
    chartType === "Number"
  );

  useEffect(() => {
    if (chartType === "Goal" && goalId) {
      const goal = goals.find((item) => item.get("id") == goalId) || fromJS(defaultGoal);
      filterDataGoalChart(projectId, goal).then((filters) => {
        setGoalFilters(filters);
      });
    }
  }, [projectId, chartType, goalId, goals]);

  useEffect(() => {
    if (chartType) {
      const groups = ruleReducer.get(viewBy) || fromJS([]);
      const segments = ruleReducer.get(segmentBy) || fromJS([{ label: t("None"), value: "None" }]);
      let chartData = initChartData;
      if (chartType === "Line") {
        if (shouldUseComparison && dataCompare) {
          const { currentData, previousData } = dataCompare;
          chartData = handleDataLineChart(
            currentData || [],
            previousData || [],
            viewBy,
            measureBy,
            dateUnit,
            groups,
            comparisonBy
          );
        } else if (dataAggregated) {
          // Original logic for non-comparison
          chartData = handleDataLineChart(
            dataAggregated,
            [],
            viewBy,
            measureBy,
            dateUnit,
            groups,
            "none"
          );
        }
      } else if (chartType === "Bar" && dataAggregated) {
        chartData = handleDataBarChart(
          dataAggregated,
          viewBy,
          measureBy,
          dateUnit,
          groups,
          segments
        );
      } else if (chartType === "Column" && dataAggregated) {
        chartData = handleDataBarChart(
          dataAggregated,
          viewBy,
          measureBy,
          dateUnit,
          groups,
          segments
        );
      } else if (chartType === "Pie" && dataAggregated) {
        chartData = handleDataPieChart(dataAggregated, viewBy, measureBy, groups, segments);
      } else if (chartType === "Goal" && dataAggregated) {
        if (goalId) {
          const goal = goals.find((item) => item.get("id") == goalId) || fromJS(defaultGoal);
          chartData = handleDataGoalChart(dataAggregated, goal, currency);
        }
      }
      if (!chartData.labels || chartData.labels.length === 0) chartData = initChartData;
      setUnifiedChartData(chartData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartType,
    reportObjectType,
    dataAggregated,
    dataCompare,
    shouldUseComparison,
    comparisonBy,
    viewBy,
    segmentBy,
    measureBy,
    dateUnit,
    goalId,
    goals,
    currency,
  ]);

  const onChartTypeChange = (type, chartType, objectType) => {
    navigate(
      `/workspaces/${projectId}/reports/dashboard/${dashboardId}/${reportId}?key=Reports&reportType=${
        type || ""
      }&reportChartType=${chartType || ""}&reportObjectType=${
        objectType || "SALE"
      }&reportViewMode=${reportViewMode}`
    );
  };

  const renderChart = useMemo(() => {
    if (chartType === "Bar") {
      return (
        <BarChart
          data={unifiedChartData}
          stacked={true}
          barSize={30}
          barColors={chartColors}
          layout={"vertical"}
          isByDate={datetimeFields.includes(viewBy)}
          labelTooltip={getViewByLabel(reportObjectType, viewBy).label}
          segmentLabelTooltip={getMeasureByLabel(reportObjectType, measureBy)?.label}
        />
      );
    } else if (chartType === "Column") {
      return (
        <BarChart
          data={unifiedChartData}
          stacked={true}
          barSize={30}
          barColors={chartColors}
          layout={"horizontal"}
          isByDate={datetimeFields.includes(viewBy)}
          labelTooltip={getViewByLabel(reportObjectType, viewBy).label}
          segmentLabelTooltip={getMeasureByLabel(reportObjectType, measureBy)?.label}
        />
      );
    } else if (chartType === "Line") {
      return <LineAreaChart data={unifiedChartData} lineColors={chartColors} mode="area" />;
    } else if (chartType === "Pie") {
      return <DonutChart data={unifiedChartData} pieColors={chartColors} />;
    } else if (chartType === "Goal") {
      const goal = goals.find((item) => item.get("id") == goalId) || fromJS(defaultGoal);
      return (
        <GoalBarChart
          data={unifiedChartData}
          goalValue={goal.getIn(["trackingGoal", "value"])}
          barSize={30}
          barColors={chartColors}
        />
      );
    }
  }, [
    chartType,
    reportObjectType,
    unifiedChartData,
    viewBy,
    measureBy,
    chartColors,
    goalId,
    goals,
  ]);

  const renderNumberData = useMemo(() => {
    const formatValue = (value: number, measureBy: string) => {
      if (measureBy === "dealValue" || measureBy === "dealValueSum") {
        return `$${value.toLocaleString()}`;
      }
      return value.toLocaleString();
    };

    const getCompareLabel = (cmp: string) => {
      switch (cmp) {
        case "lastYear":
          return t("Last Year");
        case "lastMonth":
          return t("Last Month");
        case "lastWeek":
          return t("Last Week");
        case "lastQuarter":
          return t("Last Quarter");
        default:
          return "";
      }
    };
    const compareText = comparisonBy !== "none" ? `vs. ${getCompareLabel(comparisonBy)}` : "";

    return (
      <div className="max-w-[457px] mx-auto">
        <MetricCard
          isCenter={false}
          title={reportData.get("title")}
          value={dataNumber ? formatValue(dataNumber.value, measureBy) : "0"}
          delta={comparisonBy !== "none" ? dataNumber?.delta || "" : ""}
          deltaTone={dataNumber?.deltaTone || "up"}
          isLoading={isLoadingNumber}
          icon={iconList.find((item) => item.value === chartIcons[0])?.description}
          color={chartColors[0]}
          compareText={compareText}
        />
      </div>
    );
  }, [dataNumber, isLoadingNumber, measureBy, chartIcons, chartColors, comparisonBy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm">
      <div className="p-5 space-y-5">
        {!viewModeEnabled && (
          <ChartTypeTabs
            type={reportType}
            chartType={reportChartType}
            objectType={reportObjectType}
            onChartTypeChange={onChartTypeChange}
          />
        )}
        {chartType !== "Table" && (
          <ReportHeader title={reportData.get("title")} subtitle={reportData.get("subtitle")} />
        )}
        <div className="bg-white rounded-lg">
          {chartType !== "Number" && (
            <>{isLoadingAggregated ? <ChartLoading /> : <>{renderChart}</>}</>
          )}
          {chartType === "Number" && <>{renderNumberData}</>}
        </div>
      </div>
    </div>
  );
};

export default ChartView;
